/* eslint-disable import/max-dependencies */
import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider
} from 'react-router-dom';

import { AppWrapperComponent } from './components/app-wrapper-component';
import { AuthComponent } from './components/auth-component';
import SignupCompleteScreen from './components/payment/signup-complete-screen';
import SignupPaymentScreen from './components/payment/signup-payment-screen';
import SignupPlanScreen from './components/payment/signup-plan-screen';
import SignupProfileScreen from './components/payment/signup-profile-screen';
import BeneficiaryNotificationPage from './demo-components/beneficiary-notice';
import {
  VaultEdit,
  VaultIndex,
  VaultNew,
  VaultView
} from './demo-components/vault';
import { WellnessPage } from './demo-components/wellness';
import {
  HomePage,
  LandingPage,
  LifeDocumentsPage,
  LoginPage,
  NewDocumentPage,
  PeoplePage,
  PersonalInfoForm,
  ProfilePage,
  SignUpPage,
  WillPage
} from './pages';
import {
  AdminPanel, AdminVideoPage, NewVideoPage
} from './pages/admin';
import FuneralArrangementsPage from './pages/funeral-arrangements-page';
import HealthcareDirectivesPage from './pages/healthcare-directives-page';
import {
  NewFABurialArrangements,
  NewFACeremonyDetails,
  NewFACeremonyPreferences,
  NewFACremationArrangements,
  NewFADisposition,
  NewFADonationArrangements,
  NewFAFinancialPlanning,
  NewFAIntroScreen,
  NewFAObituaryDetails,
  NewFAObituaryPreferences,
  NewFASummary,
  NewFAVerifyPersonalInfoScreen,
  NewFuneralArrangementWrapper
} from './pages/new-funeral-arrangements';
import {
  NewHDComposer,
  NewHDDocumentStorage,
  NewHDGuardianConservator,
  NewHDHealthcareAgent,
  NewHDHealthcareAgentDetails,
  NewHDHealthcareWishes,
  NewHDIntroScreen,
  NewHDOrganDonation,
  NewHDPhysicianInformation,
  NewHDPregnancyStatus,
  NewHDSummaryScreen,
  NewHDVerifyPersonalInfoScreen
} from './pages/new-healthcare-directive';
import {
  NewPOAAgentDetails,
  NewPOAAlternateAgent,
  NewPOABanking,
  NewPOABusinessOperations,
  NewPOAChoosingAgent,
  NewPOAComposer,
  NewPOADigitalAssets,
  NewPOAEstateTrust,
  NewPOAGiftRecipients,
  NewPOAGiftsIntro,
  NewPOAGiftsToAgent,
  NewPOAGiftsToOthers,
  NewPOAGovernmentBenefits,
  NewPOAGrantingPowersComplete,
  NewPOAInsuranceAnnuity,
  NewPOAIntroScreen,
  NewPOAKeepingAssetsSeparate,
  NewPOALegalActions,
  NewPOALivingTrust,
  NewPOAMultipleAgents,
  NewPOANumberAgents,
  NewPOAPayingAgent,
  NewPOAPDF,
  NewPOAPeriodReports,
  NewPOAPersonalBenefit,
  NewPOAPersonalFamilyCare,
  NewPOAPersonalInfo,
  NewPOAPetCare,
  NewPOAPowersIntro,
  NewPOARealEstate,
  NewPOAReportFrequency,
  NewPOAReportRecipients,
  NewPOAReportsComplete,
  NewPOARetirementPlans,
  NewPOASecurities,
  NewPOAStateSelection,
  NewPOASuccess,
  NewPOASummary,
  NewPOATangibleProperty,
  NewPOATaxMatters,
  NewPOAWhenEffective,
  NewPOAWhenToMake
} from './pages/new-power-of-attorney';
import {
  NewWillAddAssetScreen,
  NewWillAddDebtorScreen,
  NewWillAddPetScreen,
  NewWillAssetDistributionScreen,
  NewWillAssetSummaryScreen,
  NewWillComposer,
  NewWillDebtForgivenessScreen,
  NewWillExecutorsScreen,
  NewWillIntroScreen,
  NewWillMinorBeneficiariesScreen,
  NewWillPetCareScreen,
  NewWillSingleBeneficiaryScreen,
  NewWillSuccessScreen,
  NewWillSummaryScreen,
  NewWillVerifyPersonalInfoScreen
} from './pages/new-will';
import { PersonPage } from './pages/person';
import { PowerOfAttorneyPage } from './pages/power-of-attorney-page';
import WellnessArticlePage from './pages/wellness/article';
import ArticlesListPage from './pages/wellness/articles-list';
import WellnessVideoPage from './pages/wellness/video';
import VideosListPage from './pages/wellness/videos-list';

import '../styles/style.scss';

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Route Not Found</h1>
        <p className="text-gray-600">The page you're looking for doesn't exist.</p>
        <button className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded transition-colors">
          Go Home
        </button>
      </div>
    </div>
  );
}

function AppContainer() {
  return (
    <React.Fragment>
      <AuthComponent>
        <Outlet />
      </AuthComponent>
    </React.Fragment>
  );
}

// TODO: split out routing
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/">
      <Route path="/" element={<AppContainer />} errorElement={<NotFoundPage />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/" element={<AppWrapperComponent />}>
          <Route path="/signup/profile" element={<SignupProfileScreen />} />
          <Route path="/signup/plan" element={<SignupPlanScreen />} />
          <Route path="/signup/payment" element={<SignupPaymentScreen />} />
          <Route path="/signup/complete" element={<SignupCompleteScreen />} />  
          <Route path="/home" element={<HomePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profiles/new" element={<PersonalInfoForm create={true} />} />
          <Route path="/vault" element={<VaultIndex />} />
          <Route path="/vault/new" element={<VaultNew />} />
          <Route path="/vault/:id" element={<VaultView />} />
          <Route path="/vault/:id/edit" element={<VaultEdit />} />
          <Route path="/documents/new" element={<NewDocumentPage />} />
          <Route path="/profiles/self" element={<ProfilePage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/people/:personId" element={<PersonPage />} />
          <Route path="/people/:personId/life-documents" element={<LifeDocumentsPage />} />
          <Route path="/people/:personId/life-documents/will" element={<WillPage />} />
          <Route path="/people/:personId/life-documents/will/new" element={<NewWillComposer />}>
            <Route index element={<NewWillIntroScreen />} />
            <Route path="verify" element={<NewWillVerifyPersonalInfoScreen />} />
            <Route path="asset-distribution" element={<NewWillAssetDistributionScreen />} />
            <Route path="single-beneficiary" element={<NewWillSingleBeneficiaryScreen />} />
            <Route path="asset-summary" element={<NewWillAssetSummaryScreen />} />
            <Route path="add-asset" element={<NewWillAddAssetScreen />} />
            <Route path="minor-beneficiaries" element={<NewWillMinorBeneficiariesScreen />} />
            <Route path="pet-arrangements" element={<NewWillPetCareScreen />} />
            <Route path="add-pet" element={<NewWillAddPetScreen />} />
            <Route path="debt-forgiveness" element={<NewWillDebtForgivenessScreen />} />
            <Route path="add-debtor" element={<NewWillAddDebtorScreen />} />
            <Route path="executors" element={<NewWillExecutorsScreen />} />
            <Route path="review" element={<NewWillSummaryScreen />} />
            <Route path="success" element={<NewWillSuccessScreen />} />
            <Route path="continue" element={<NewWillSummaryScreen isContinue={true} />} />
          </Route>
          <Route path="/people/:personId/life-documents/funeral-arrangements" element={<FuneralArrangementsPage />} />
          <Route path="/people/:personId/life-documents/funeral-arrangements/new" element={<NewFuneralArrangementWrapper />}>
            <Route index element={<NewFAIntroScreen />} />
            {/* TODO: move verify personal info screen */}
            <Route path="verify" element={<NewFAVerifyPersonalInfoScreen />} />
            <Route path="disposition" element={<NewFADisposition />} />
            <Route path="burial-arrangements" element={<NewFABurialArrangements />} />
            <Route path="cremation-arrangements" element={<NewFACremationArrangements />} />
            <Route path="donation-arrangements" element={<NewFADonationArrangements />} />
            <Route path="ceremony-preferences" element={<NewFACeremonyPreferences />} />
            <Route path="ceremony-details" element={<NewFACeremonyDetails />} />
            <Route path="obituary-preferences" element={<NewFAObituaryPreferences />} />
            <Route path="obituary-details" element={<NewFAObituaryDetails />} />
            <Route path="financial-planning" element={<NewFAFinancialPlanning />} />
            <Route path="summary" element={<NewFASummary />} />
            <Route path="continue" element={<NewFASummary isContinue={true} />} />
          </Route>
          <Route path="/people/:personId/life-documents/healthcare-directive" element={<HealthcareDirectivesPage />} />
          <Route path="/people/:personId/life-documents/healthcare-directive/new" element={<NewHDComposer />}>
            <Route index element={<NewHDIntroScreen />} />
            <Route path="verify" element={<NewHDVerifyPersonalInfoScreen />} />
            <Route path="pregnancy-status" element={<NewHDPregnancyStatus />} />
            <Route path="physician-information" element={<NewHDPhysicianInformation />} />
            <Route path="healthcare-agent" element={<NewHDHealthcareAgent />} />
            <Route path="healthcare-agent-details" element={<NewHDHealthcareAgentDetails />} />
            <Route path="guardian-conservator" element={<NewHDGuardianConservator />} />
            <Route path="document-storage" element={<NewHDDocumentStorage />} />
            <Route path="healthcare-wishes" element={<NewHDHealthcareWishes />} />
            <Route path="organ-donation" element={<NewHDOrganDonation />} />
            <Route path="continue" element={<NewHDSummaryScreen isContinue={true} />} />
            <Route path="summary" element={<NewHDSummaryScreen isContinue={false} />} />
          </Route>
          <Route path="/people/:personId/life-documents/other-bequests" element={<div>Other Bequests</div>} />
          <Route path="/people/:personId/life-documents/other-bequests/new" element={<div>Create Other Bequests</div>} />
          <Route path="/people/:personId/life-documents/power-of-attorney" element={<PowerOfAttorneyPage />} />
          <Route path="/people/:personId/life-documents/power-of-attorney/new" element={<NewPOAComposer />}>
            <Route index element={<NewPOAStateSelection />} />
            <Route path="intro" element={<NewPOAIntroScreen />} />
            <Route path="when-to-make" element={<NewPOAWhenToMake />} />
            <Route path="when-effective" element={<NewPOAWhenEffective />} />
            <Route path="personal-info" element={<NewPOAPersonalInfo />} />
            <Route path="choosing-agent" element={<NewPOAChoosingAgent />} />
            <Route path="multiple-agents" element={<NewPOAMultipleAgents />} />
            <Route path="number-agents" element={<NewPOANumberAgents />} />
            <Route path="agent-details" element={<NewPOAAgentDetails />} />
            <Route path="alternate-agent" element={<NewPOAAlternateAgent />} />
            <Route path="powers-intro" element={<NewPOAPowersIntro />} />
            
            {/* Financial Powers - in Summary order */}
            <Route path="banking" element={<NewPOABanking />} />
            <Route path="securities" element={<NewPOASecurities />} />
            <Route path="insurance-annuity" element={<NewPOAInsuranceAnnuity />} />
            <Route path="retirement-plans" element={<NewPOARetirementPlans />} />
            <Route path="tax-matters" element={<NewPOATaxMatters />} />
            
            {/* Property Powers - in Summary order */}
            <Route path="real-estate" element={<NewPOARealEstate />} />
            <Route path="tangible-property" element={<NewPOATangibleProperty />} />
            <Route path="digital-assets" element={<NewPOADigitalAssets />} />
            
            {/* Business & Legal Powers - in Summary order */}
            <Route path="business-operations" element={<NewPOABusinessOperations />} />
            <Route path="estate-trust" element={<NewPOAEstateTrust />} />
            <Route path="living-trust" element={<NewPOALivingTrust />} />
            <Route path="legal-actions" element={<NewPOALegalActions />} />
            
            {/* Personal Care Powers - in Summary order */}
            <Route path="personal-family-care" element={<NewPOAPersonalFamilyCare />} />
            <Route path="pet-care" element={<NewPOAPetCare />} />
            <Route path="government-benefits" element={<NewPOAGovernmentBenefits />} />
            
            {/* Gift Flow */}
            <Route path="gifts-intro" element={<NewPOAGiftsIntro />} />
            <Route path="gifts-to-agent" element={<NewPOAGiftsToAgent />} />
            <Route path="gifts-to-others" element={<NewPOAGiftsToOthers />} />
            <Route path="gift-recipients" element={<NewPOAGiftRecipients />} />
            <Route path="granting-powers-complete" element={<NewPOAGrantingPowersComplete />} />
            
            {/* Additional Options */}
            <Route path="period-reports" element={<NewPOAPeriodReports />} />
            <Route path="report-recipients" element={<NewPOAReportRecipients />} />
            <Route path="report-frequency" element={<NewPOAReportFrequency />} />
            <Route path="reports-complete" element={<NewPOAReportsComplete />} />
            <Route path="personal-benefit" element={<NewPOAPersonalBenefit />} />
            <Route path="keeping-assets-separate" element={<NewPOAKeepingAssetsSeparate />} />
            <Route path="paying-agent" element={<NewPOAPayingAgent />} />
            
            {/* Summary and Completion */}
            <Route path="summary" element={<NewPOASummary />} />
            <Route path="pdf" element={<NewPOAPDF />} />
            <Route path="success" element={<NewPOASuccess />} />
            <Route path="continue" element={<NewPOASummary isContinue={true} />} />
          </Route>
          <Route path="/people/:personId/life-documents/letter-to-survivors" element={<div>Letter to Survivors</div>} />
          <Route path="/people/:personId/life-documents/letter-to-survivors/new" element={<div>Create Letter to Survivors</div>} />
          <Route path="/people/:personId/life-documents/living-trust" element={<div>Living Trust</div>} />
          <Route path="/people/:personId/life-documents/living-trust/new" element={<div>Create Living Trust</div>} />
          <Route path="/people/:personId/life-documents/transfer-on-death-deed" element={<div>Transfer on Death Deed</div>} />
          <Route path="/people/:personId/life-documents/transfer-on-death-deed/new" element={<div>Create Transfer on Death Deed</div>} />
          <Route path="/people/:personId/life-documents/estate-planning" element={<div>Estate Planning</div>} />
          <Route path="/people/:personId/life-documents/estate-planning/new" element={<div>Create Estate Planning</div>} />
          <Route path="/people/:personId/life-documents/financial-documents" element={<div>Financial Documents</div>} />
          <Route path="/people/:personId/life-documents/financial-documents/new" element={<div>Create Financial Documents</div>} />
          <Route path="/people/:personId/life-documents/home-and-family-documents" element={<div>Home and Family Documents</div>} />
          <Route path="/people/:personId/life-documents/home-and-family-documents/new" element={<div>Create Home and Family Documents</div>} />
          <Route path="/wellness" element={<WellnessPage />} />
          <Route path="/wellness/articles" element={<ArticlesListPage />} />
          <Route path="/wellness/articles/:articleId" element={<WellnessArticlePage />} />
          <Route path="/wellness/videos" element={<VideosListPage />} />
          <Route path="/wellness/videos/:videoId" element={<WellnessVideoPage />} />
          <Route path="/people/:personId/life-documents/will/:id" element={<div>Will Page</div>} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/videos" element={<AdminVideoPage />} />
          <Route path="/admin/videos/new" element={<NewVideoPage />} />
        </Route>
      </Route>
      <Route path="/users/:userId/people/:personId/beneficiary-notice" element={<BeneficiaryNotificationPage />} />
      {/* <Route path="/admin/api" element={<Api />} errorElement={<NotFoundPage />}>
        <Route path="/admin/api" element={<Directory />} />
        <Route path="/admin/api/:resource" element={<DynamicList />} />
        <Route path="/admin/api/:resource/new" element={<New />} />
        <Route path="/admin/api/:resource/:id" element={<Show />} />
        <Route path="/admin/api/:resource/:id/*" element={<DynamicList />} />
      </Route> */}
    </Route>
  )
);

export function App() {
  return <RouterProvider router={router} />;
}

export default App;
